import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import {
  IFormThingsToImprove,
  InitiativeStatus,
} from 'legacy/lut/classes/models/partner360/IFormThingsToImprove'
import { normalizeBusinessPriorityFormValues } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/businessPriorities/helpers'
import { mapPersistedDescriptionQualityToResult } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/descriptionQuality/mappers'
import { TInvestmentSourceValue } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/investmentSource/models'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { normalizeKeyInitiativeStatus } from 'partner360/entities/keyInitiatives/model/status'

import { PRACTICE_DEVELOPMENT } from '../configs'
import { IKeyInitiativeForm } from '../models/keyInitiativeForm'

import {
  getIsPracticeDevelopment,
  getPracticeDevelopmentProgressValues,
} from './getPracticeDevelopmentProgressValues'
import {
  EKeyInitiativeGoalMode,
  getPersistedKeyInitiativeGoalMode,
  shouldAddToBridgesForGoalMode,
} from './keyInitiativeGoalMode'
import { normalizeKeyInitiativeOutcome } from './keyInitiativeOutcome'

export const mapKeyInitiativeToForm = (
  data: Partial<IFormThingsToImprove>,
  businessPriorities: IKIBusinessPriority[] = [],
  keyObjectives: IKeyObjective[] = [],
  newInitiativeGoalMode = EKeyInitiativeGoalMode.RunRate
) => {
  const rawBusinessPriorities =
    data.BusinessPriorities?.split(',')
      .map((item) => item.trim())
      .filter(Boolean) || []
  const keyObjective = data.Development || ''
  const isPracticeDevelopment = getIsPracticeDevelopment(
    keyObjective,
    keyObjectives,
    data.KeyObjectiveCategory === PRACTICE_DEVELOPMENT
  )
  const progressValues = isPracticeDevelopment
    ? getPracticeDevelopmentProgressValues(data.CurrentProgress)
    : null

  const form: IKeyInitiativeForm = {
    entryId: data.EntryId,
    previousEntryId: data.PreviousEntryId ?? null,
    isPracticeDevelopment,
    isNotApplicable: Boolean(data.IsNotApplicable),
    isFromGlobalPartner: Boolean(data.IsFromGlobalPartner),
    name: data.Name || '',
    businessUnit: data.BusinessUnit || '',
    practiceBuilder: data.PracticeBuilder ?? '',
    currency: data.Currency || '',
    description: data.Description || '',
    descriptionQuality: mapPersistedDescriptionQualityToResult(
      data.DescriptionQuality,
      data.Description
    ),
    currentProgress: progressValues?.currentProgress ?? data.CurrentProgress ?? '',
    followUp: data.FollowUp ?? progressValues?.followUp ?? '',
    activityType: data.ActivityType || '',
    focusArea: data.FocusArea || '',

    goalNdp: data.GoalNdp || 0,
    goalNdpUsd: data.GoalNdpUSD || 0,
    goalNdpLocalCurrency: data.GoalNdpLocalCurrency || 0,
    goalNet: data.GoalNet || 0,
    goalNetUsd: data.GoalNetUSD || 0,
    goalNetLocalCurrency: data.GoalNetLocalCurrency || 0,
    goalTNet: data.GoalTNet || 0,
    goalTNetUsd: data.GoalTNetUSD || 0,
    goalTNetLocalCurrency: data.GoalTNetLocalCurrency || 0,
    goalUTNet: data.GoalUTNet || 0,
    goalUTNetUsd: data.GoalUTNetUSD || 0,
    goalUTNetLocalCurrency: data.GoalUTNetLocalCurrency || 0,

    incrementalNdp: data.IncrementalNdp || 0,
    incrementalNdpUsd: data.IncrementalNdpUSD || 0,
    incrementalNdpLocalCurrency: data.IncrementalNdpLocalCurrency || 0,
    incrementalNet: data.IncrementalNet || 0,
    incrementalNetUsd: data.IncrementalNetUSD || 0,
    incrementalNetLocalCurrency: data.IncrementalNetLocalCurrency || 0,
    incrementalTNet: data.IncrementalTNet || 0,
    incrementalTNetUsd: data.IncrementalTNetUSD || 0,
    incrementalTNetLocalCurrency: data.IncrementalTNetLocalCurrency || 0,
    incrementalUTNet: data.IncrementalUTNet || 0,
    incrementalUTNetUsd: data.IncrementalUTNetUSD || 0,
    incrementalUTNetLocalCurrency: data.IncrementalUTNetLocalCurrency || 0,

    justification: data.Justification || '',

    hpInvestmentSource: data.HPInvestmentSource || ('' as TInvestmentSourceValue),
    hpInvestmentSum: data.HPInvestmentSum || 0,
    isDataInsightRelated: data.DataInsightRelated === 1,
    keyObjective,
    businessPriorities: normalizeBusinessPriorityFormValues(
      rawBusinessPriorities,
      businessPriorities
    ),
    keyObjectiveTags: data.KeyObjectiveTags?.split(',') || [],
    owner: data.Owner || '',
    period: data.Period?.split(',') || [],
    shouldAddToBridges: data.AddToBridges === 1,
    status:
      normalizeKeyInitiativeStatus(data.Status) ||
      (data.EntryId == null ? InitiativeStatus.New : ''),
    valueType: data.ValueType || '',
    outcome: normalizeKeyInitiativeOutcome(data.Outcome),
    outcomeValueType: data.OutcomeValueType ?? null,
    funnelSize: data.FunnelSize ?? null,
    leads: data.Leads ?? null,
    conversionRate: data.ConversionRate ?? null,
    attendance: data.Attendance ?? null,

    initType: data.InitType || null,
    isAchieved: typeof data.IsAchieved === 'number' ? data.IsAchieved === 1 : null,
    goalDescription: data.GoalDescription || '',
    proofOfExecution: data.ProofOfExecution || '',
  }

  if (data.HPInvestmentSum !== null && data.HPInvestmentSource !== null) {
    form.hpInvestmentSource = data.HPInvestmentSource
    form.hpInvestmentSum = data.HPInvestmentSum
  }

  const isNewInitiative = data.EntryId == null
  form.goalMode = isNewInitiative ? newInitiativeGoalMode : getPersistedKeyInitiativeGoalMode(data)
  if (isNewInitiative) {
    form.shouldAddToBridges = shouldAddToBridgesForGoalMode(form.goalMode)
  }

  return form
}
